import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Alert, Button } from "reactstrap";
import { useAtomValue } from "jotai";

import BrieConversationAPI from "../../../api/api/BrieConversationAPI";
import GlobalInstructionsMessage from "./GlobalInstructionsMessage";
import { Instruction, useBrieGPTProjects } from "./useBrieGPTItems";
import { BrieGPTTabs } from "./BrieGPTSidebarElements/Types";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import { newToastEvent } from "../../../utils/ToastUtil";
import { DrawerProps } from "../../Drawers/DrawerTypes";
import ManageItems from "../../ManageItems/ManageItems";
import AppSpinner from "../../../elements/AppSpinner";
import { BrieTypeProps } from "./BrieGPTSidebar";
import AppDrawer from "../../Drawers/AppDrawer";
import StrUtils from "../../../utils/StrUtils";
import AppInput from "../../AppInput";

type Props = DrawerProps &
  BrieTypeProps & { tab: BrieGPTTabs; projectID: string | null };

export default function ProjectSettingsDrawer({
  open,
  onClose,
  projectID,
  tab,
  ...brieTypeProps
}: Props) {
  const { projects } = useBrieGPTProjects({ tab, ...brieTypeProps });
  const project = projects?.find((p) => p?.id === projectID);
  const [projectName, setProjectName] = useState(project?.Name || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Use an array of strings for instructions
  const [instructionList, setInstructionList] = useState<Instruction[]>([]);
  const client = useQueryClient();
  const tenantID = useAtomValue(tenantIDAtom);
  const instructionsRef = useRef<HTMLTextAreaElement>(null);

  // Sync project name with state
  useEffect(() => {
    setProjectName(project?.Name || "");
  }, [project?.Name]);

  // Sync instructions with state
  useEffect(() => {
    setInstructionList(project?.InstructionList || []);
  }, [project?.InstructionList]);

  const changesMade =
    projectName !== project?.Name ||
    JSON.stringify(instructionList) !==
      JSON.stringify(project?.InstructionList);

  return (
    <AppDrawer
      open={open}
      onClose={handleClose}
      headerTitle="Project Settings"
      headerOptions={
        <Button
          onClick={updateProject}
          color="primary"
          size="sm"
          className="tw:w-24"
          disabled={saving || !changesMade}
        >
          {saving ? <AppSpinner size="sm" color="white" /> : "Save"}
        </Button>
      }
      body={
        <>
          <h4>Project Name</h4>
          <AppInput
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
          />
          <h4 className="tw:mb-0 tw:mt-5">Project Instructions</h4>
          <div className="text-sm">
            Any instructions you add here will automatically apply to all chats
            created within the project, ensuring consistent guidance and shared
            context across your conversations.
          </div>
          <AppInput
            innerRef={instructionsRef}
            className="tw:mt-2"
            type="textarea"
            placeholder="Add Instructions"
            rows={3}
            onKeyDown={handleKeyDown}
          />
          <div className="tw:my-3 tw:flex tw:justify-end">
            <Button
              color="primary"
              size="sm"
              className="tw:flex tw:items-center"
              onClick={addInstruction}
            >
              Add
              <i className="mdi mdi-keyboard-return tw:pl-2 tw:text-lg" />
            </Button>
          </div>
          {error && (
            <Alert color="danger" className="tw:my-4 tw:py-2">
              {error}
            </Alert>
          )}
          <GlobalInstructionsMessage />
          <ManageItems
            itemKey={undefined}
            nameKey={"Instruction"}
            items={instructionList}
            onReorder={({ newOrder }) => handleReorder(newOrder)}
            title="Instruction"
            description="No instructions have been added."
            onDelete={({ id: instruction }) => {
              setInstructionList((prev) =>
                prev.filter((i) => i.id !== instruction)
              );
            }}
            getColor={(i) =>
              i?.Project ? "var(--tertiary)" : "var(--primary-light-bg)"
            }
            useTooltip
          />
        </>
      }
    />
  );

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      if (e.shiftKey) {
        // Shift + Enter: Add a new line
        e.preventDefault();
        instructionsRef.current!.value += "\n";
      } else {
        // Enter: Submit the message
        e.preventDefault();
        addInstruction();
      }
    }
  }

  function addInstruction() {
    const instruction = instructionsRef.current?.value;

    if (!instruction) return;

    // Only add the instruction if it doesn't already exist
    if (instructionList.find((i) => i.Instruction === instruction)) {
      return setError("Instruction already exists.");
    }

    const newInstruction = {
      id: StrUtils.uuidv4(),
      Instruction: instruction,
      Project: true,
    };

    setInstructionList((prev) => [...prev, newInstruction]);
    instructionsRef.current!.value = "";
  }

  function handleReorder(newOrder: string[]) {
    const newInstructions = newOrder.map((id) => {
      const instruction = instructionList.find((i) => i.id === id)!;
      return instruction;
    });

    setInstructionList(newInstructions);
  }

  function handleClose() {
    onClose();
  }

  async function updateProject() {
    if (!projectID) return newToastEvent("error", "No project selected.");

    setSaving(true);
    setError("");

    const payload: Record<string, any> = {};

    if (projectName !== project?.Name) {
      payload.Name = projectName;
    }
    if (
      JSON.stringify(instructionList) !==
      JSON.stringify(project?.InstructionList)
    ) {
      payload.InstructionList = instructionList;
    }

    try {
      await BrieConversationAPI.updateBrieGPTProject({
        projectID: projectID,
        payload: payload,
      });

      const itemID =
        brieTypeProps.type === "view"
          ? brieTypeProps.viewID
          : brieTypeProps.recordID;

      client.invalidateQueries({
        queryKey: [tenantID, itemID, "BrieGPTChats"],
      });
      handleClose();
      newToastEvent("success", "Project updated successfully.");
    } catch (e) {
      console.error(e);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setSaving(false);
    }
  }
}
